import MAP_RULES from "./mapRules.json";
import { isHavenIsland } from "./hotspotPopupContent";

const BUILDING_MODELS = MAP_RULES.buildingModels || [];
const MODELS_BY_ID = new Map(BUILDING_MODELS.map((m) => [m.id, m]));
const PALETTES = new Map((MAP_RULES.colorPalettes || []).map((p) => [p.slot, (p.colors || []).map((c) => c.id)]));
const RESIDENTIAL = "residential";

export function getBuildingModel(modelId) {
  return MODELS_BY_ID.get(modelId) || MODELS_BY_ID.get(MAP_RULES.defaultBuildingModel) || null;
}

function seededRandom(seed) {
  let h = 2166136261;
  const s = String(seed || "");
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return () => {
    h = (h + 0x6d2b79f5) | 0;
    let t = Math.imul(h ^ (h >>> 15), 1 | h);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const pick = (list, random) => list[Math.floor(random() * list.length)];

export function buildingFitsModel(building, model) {
  const floors = building?.floors || [];
  if (model.maxFloors > 0 && floors.length > model.maxFloors) return false;
  return floors.every((f) => (f?.rooms || []).length <= model.roomsPerFloor);
}

function pickModelId(building, usedResidential, random) {
  const fitting = BUILDING_MODELS.filter((m) => buildingFitsModel(building, m));
  const offices = fitting.filter((m) => m.category !== RESIDENTIAL);
  const freeResidential = fitting.filter((m) => m.category === RESIDENTIAL && !usedResidential.has(m.id));
  const wantsResidential = random() < 0.5;
  const pool = (wantsResidential && freeResidential.length) || !offices.length
    ? (freeResidential.length ? freeResidential : fitting)
    : offices;
  return pool.length ? pick(pool, random).id : MAP_RULES.defaultBuildingModel;
}

function pickColors(building, model, usedColors, random) {
  const colors = { ...(building.colors && typeof building.colors === "object" ? building.colors : {}) };
  for (const slot of model?.colorSlots || []) {
    const palette = PALETTES.get(slot) || [];
    if (!palette.length || palette.includes(colors[slot])) continue;
    const used = usedColors.get(slot) || new Set();
    const free = palette.filter((c) => !used.has(c));
    colors[slot] = pick(free.length ? free : palette, random);
    used.add(colors[slot]);
    usedColors.set(slot, used);
  }
  return colors;
}

function needsStyle(building) {
  const model = MODELS_BY_ID.get(building?.model_id);
  if (!model) return true;
  return (model.colorSlots || []).some((slot) => !(PALETTES.get(slot) || []).includes(building.colors?.[slot]));
}

function repeatedResidential(buildings) {
  const seen = new Set();
  const repeated = new Set();
  for (const building of buildings) {
    const model = MODELS_BY_ID.get(building.model_id);
    if (model?.category !== RESIDENTIAL) continue;
    if (seen.has(model.id)) repeated.add(building);
    seen.add(model.id);
  }
  return repeated;
}

export function needsBuildingModels(districts) {
  if (!BUILDING_MODELS.length) return false;
  return (districts || []).some((district) =>
    (district?.islands || []).some((island) => {
      if (isHavenIsland(island)) return false;
      const buildings = (island?.buildings || []).filter(Boolean);
      return buildings.some(needsStyle) || repeatedResidential(buildings).size > 0;
    })
  );
}

export function applyBuildingModels(districts) {
  if (!BUILDING_MODELS.length) return districts;
  for (const district of districts || []) {
    for (const island of district?.islands || []) {
      const buildings = (island?.buildings || []).filter(Boolean);
      const repeated = repeatedResidential(buildings);
      if (!repeated.size && !buildings.some(needsStyle)) continue;
      for (const building of repeated) delete building.model_id;
      const usedResidential = new Set();
      const usedColors = new Map();
      for (const building of buildings) {
        const model = MODELS_BY_ID.get(building.model_id);
        if (model?.category === RESIDENTIAL) usedResidential.add(model.id);
        for (const [slot, color] of Object.entries(building.colors || {})) {
          if (!usedColors.has(slot)) usedColors.set(slot, new Set());
          usedColors.get(slot).add(color);
        }
      }
      for (const building of buildings) {
        if (!needsStyle(building)) continue;
        const random = seededRandom(building.id || building.name);
        if (!MODELS_BY_ID.has(building.model_id)) {
          building.model_id = pickModelId(building, usedResidential, random);
          const model = MODELS_BY_ID.get(building.model_id);
          if (model?.category === RESIDENTIAL) usedResidential.add(model.id);
        }
        building.colors = pickColors(building, MODELS_BY_ID.get(building.model_id), usedColors, random);
      }
    }
  }
  return districts;
}

export function carryBuildingModels(districts, previousDistricts) {
  const previous = new Map();
  for (const district of previousDistricts || []) {
    for (const island of district?.islands || []) {
      for (const building of island?.buildings || []) {
        if (building?.id && building.model_id) previous.set(building.id, building);
      }
    }
  }
  if (!previous.size) return districts;
  return (districts || []).map((district) => ({
    ...district,
    islands: (district.islands || []).map((island) => ({
      ...island,
      buildings: (island.buildings || []).map((building) => {
        const before = previous.get(building?.id);
        if (!before || building.model_id) return building;
        return { ...building, model_id: before.model_id, ...(before.colors ? { colors: before.colors } : {}) };
      }),
    })),
  }));
}

export function withAssignedColors(districts, assignments) {
  const byId = new Map((assignments || []).filter((a) => a?.buildingID && a.colors).map((a) => [String(a.buildingID), a.colors]));
  if (!byId.size) return districts;
  let changed = false;
  const next = (districts || []).map((district) => ({
    ...district,
    islands: (district.islands || []).map((island) => ({
      ...island,
      buildings: (island.buildings || []).map((building) => {
        const colors = byId.get(String(building?.id));
        if (!colors) return building;
        const merged = { ...(building.colors || {}), ...colors };
        if (JSON.stringify(merged) === JSON.stringify(building.colors || {})) return building;
        changed = true;
        return { ...building, colors: merged };
      }),
    })),
  }));
  return changed ? next : districts;
}
